import { Link } from "react-router-dom";
import { ArrowLeft, Shield, Database, Cookie, Bot, Phone, Upload, Lock, Clock, UserCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import Footer from "@/components/Footer";

const Section = ({ icon: Icon, title, children }) => (
  <section className="bg-white border border-[#4A331D]/10 rounded-xl p-5 md:p-7 shadow-sm">
    <div className="flex items-center gap-3 mb-4">
      <div className="w-9 h-9 rounded-lg bg-[#4A331D]/8 flex items-center justify-center text-[#4A331D]">
        <Icon className="w-4 h-4" />
      </div>
      <h2 className="text-lg md:text-xl font-bold text-[#4A331D] tracking-tight">{title}</h2>
    </div>
    <div className="prose prose-sm md:prose-base max-w-none text-gray-700 leading-relaxed space-y-3">
      {children}
    </div>
  </section>
);

const Privacy = () => {
  const updated = "14. Mai 2026";
  return (
    <div className="min-h-screen bg-[#FBF9F5] flex flex-col">
      <div className="flex-grow">
        <div className="max-w-4xl mx-auto px-4 py-8 md:py-12">
          <Link to="/">
            <Button variant="outline" className="mb-6" data-testid="back-home-button">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Zurueck zur Startseite
            </Button>
          </Link>

          <header className="mb-8">
            <div className="text-xs text-[#4A331D]/60 uppercase tracking-wider mb-2">Rechtliches</div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#4A331D] tracking-tight mb-2">
              Datenschutzerklaerung
            </h1>
            <p className="text-sm text-gray-600">Letzte Aktualisierung: {updated}</p>
          </header>

          <div className="space-y-5">
            {/* 1 - Verantwortlicher */}
            <Section icon={UserCheck} title="1. Verantwortlicher">
              <p>Verantwortlich fuer die Datenverarbeitung auf dieser Website ist:</p>
              <p>
                <strong>BEKAJ BAU</strong><br />
                Inhaber: Elson Bekaj<br />
                E-Mail: <a href="mailto:kontakt@bekajbau.de" className="text-[#4A331D] underline">kontakt@bekajbau.de</a><br />
                Telefon: <a href="tel:+4917620283482" className="text-[#4A331D] underline">+49 176 20283482</a><br />
                Vollstaendige Anschrift: siehe <Link to="/impressum" className="text-[#4A331D] underline">Impressum</Link>
              </p>
            </Section>

            {/* 2 - Datenarten und Zwecke */}
            <Section icon={Database} title="2. Welche Daten wir verarbeiten und wozu">
              <p>Wir verarbeiten ausschliesslich die fuer den jeweiligen Zweck erforderlichen Daten:</p>

              <h3 className="font-semibold text-[#4A331D] mt-4">2.1 Schnellanfrage- und Projekt-Formular</h3>
              <ul className="list-disc pl-5">
                <li>Name, Telefonnummer, E-Mail-Adresse</li>
                <li>Projektart, Beschreibung, gewuenschter Zeitrahmen, Ort/PLZ</li>
                <li>Optional: Etage, Zugang zur Baustelle, hochgeladene Fotos/Videos/PDFs</li>
                <li>Technische Daten (IP-Adresse, User-Agent, Zeitstempel)</li>
              </ul>
              <p>
                <strong>Zweck:</strong> Bearbeitung Ihrer Anfrage, Erstellung eines Angebots, Terminvereinbarung.<br />
                <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. b DSGVO (vorvertragliche Massnahmen) sowie Art. 6 Abs. 1 lit. a DSGVO (Einwilligung fuer optionale Felder).
              </p>

              <h3 id="ki" className="font-semibold text-[#4A331D] mt-4 scroll-mt-24">2.2 Projekt-Assistent (KI-gestuetzter Chat) &ndash; Transparenz nach EU AI Act (Art. 50)</h3>
              <p className="text-[13px] bg-[#FFF8EC] border border-[#D4AA6A]/40 rounded-lg p-3 mb-2">
                <strong>KI-Kennzeichnung:</strong> Unser Projekt-Assistent ist ein KI-System im Sinne der
                Verordnung (EU) 2024/1689 (EU AI Act). Beim Start des Chats werden Sie darueber transparent
                informiert, dass Sie mit einer KI und nicht mit einem Menschen kommunizieren. Rechtsgrundlage
                der Kennzeichnung: Art. 50 Abs. 1 EU AI Act.
              </p>
              <ul className="list-disc pl-5">
                <li>Ihre Chat-Eingaben (Texte, Antwortauswahlen)</li>
                <li>Optional hochgeladene Fotos/Videos/PDFs</li>
                <li>Session-Metadaten: Geraetetyp, Browser, Quelle (Referrer, UTM-Parameter), Aufenthaltsdauer</li>
                <li>Erst nach ausdruecklicher Zustimmung: Name, Telefon, E-Mail, Ort</li>
              </ul>
              <p>
                <strong>Hinweis zur KI-Verarbeitung:</strong> Der Projekt-Assistent nutzt grosse Sprachmodelle (LLMs)
                der Drittanbieter <strong>Google (Gemini)</strong> und <strong>OpenAI (GPT)</strong>,
                eingebunden ueber den Dienst Emergent. Ihre Chat-Eingaben werden zum Zweck der Antwortgenerierung
                automatisiert an diese Anbieter uebermittelt. Die Anbieter koennen sich ausserhalb des EWR
                befinden; die Uebermittlung erfolgt auf Grundlage der EU-Standardvertragsklauseln.
              </p>
              <p>
                <strong>Wichtig:</strong> Bitte geben Sie keine besonderen Kategorien personenbezogener Daten
                (Gesundheit, Religion, politische Meinung etc.) oder sensiblen Inhalte in den Chat ein.
                Eingaben werden ausschliesslich zur Bearbeitung Ihrer Anfrage verwendet und nicht zum Training
                fremder KI-Modelle freigegeben.
              </p>
              <p>
                <strong>Zweck:</strong> Strukturierte Projektaufnahme, Lead-Qualifizierung, automatisierte Zusammenfassung fuer unser Backoffice.<br />
                <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. a (Einwilligung) und lit. b DSGVO (vorvertraglich).
              </p>

              <h3 className="font-semibold text-[#4A331D] mt-4">2.3 WhatsApp-Kommunikation</h3>
              <p>
                Bei Klick auf einen WhatsApp-Button werden Sie zur Plattform WhatsApp (Meta Platforms Ireland Ltd.)
                weitergeleitet. Wir uebertragen lediglich eine vorausgefuellte Nachricht; alle weiteren
                Datenverarbeitungen erfolgen ausschliesslich durch WhatsApp/Meta nach deren Datenschutzbestimmungen
                (<a href="https://www.whatsapp.com/legal/privacy-policy-eea" target="_blank" rel="noreferrer" className="text-[#4A331D] underline">privacy-policy-eea</a>).
              </p>

              <h3 className="font-semibold text-[#4A331D] mt-4">2.4 Telefon-Klicks</h3>
              <p>
                Klicks auf Telefonnummern oeffnen die Wahlfunktion Ihres Geraetes. Wir erfassen pseudonym die
                Click-Anzahl als Conversion-Event (nur nach Marketing-Einwilligung).
              </p>

              <h3 className="font-semibold text-[#4A331D] mt-4">2.5 Datei-Uploads (Bilder, Videos, PDFs)</h3>
              <p>
                Hochgeladene Dateien dienen ausschliesslich der Projekteinschaetzung. Erlaubte Formate:
                JPG, PNG, WEBP, HEIC, MP4, MOV, PDF. Maximalgroesse je Datei: 15 MB. Dateien werden
                serverseitig MIME- und groessengeprueft; ausfuehrbare Inhalte werden abgelehnt. Speicherung
                erfolgt verschluesselt im Object Storage von Emergent.
              </p>
            </Section>

            {/* 3 - KI */}
            <Section icon={Bot} title="3. Automatisierte Verarbeitung und KI">
              <p>
                Der Projekt-Assistent erstellt automatisiert eine kurze Zusammenfassung Ihres Anliegens und
                eine interne Lead-Bewertung (Score 1-10) zur Priorisierung im Backoffice. Diese Verarbeitung
                hat <strong>keine rechtliche Wirkung</strong> Ihnen gegenueber und stellt keine automatisierte
                Einzelentscheidung im Sinne von Art. 22 DSGVO dar - die Kontaktaufnahme erfolgt stets durch einen
                Menschen aus unserem Team.
              </p>
            </Section>

            {/* 4 - Tracking */}
            <Section icon={Cookie} title="4. Cookies, Tracking & Consent Mode v2">
              <p>
                Wir setzen Tracking-Technologien <strong>ausschliesslich nach Ihrer ausdruecklichen Einwilligung</strong>
                ueber unser Cookie-Banner ein. Vor einer Einwilligung werden keinerlei Werbe- oder Analyse-Cookies gesetzt.
              </p>

              <h3 className="font-semibold text-[#4A331D] mt-4">4.1 Eingesetzte Dienste (nur nach Einwilligung)</h3>
              <ul className="list-disc pl-5">
                <li><strong>Google Tag Manager / Google Ads</strong> (Google Ireland Ltd.) - Conversion-Tracking fuer Anzeigenkampagnen</li>
                <li><strong>Google Analytics 4 (GA4)</strong> - pseudonyme Reichweitenmessung</li>
                <li><strong>Meta Pixel</strong> (Meta Platforms Ireland Ltd.) - Conversion-Messung fuer Meta-Anzeigen</li>
                <li><strong>Meta Conversion API (CAPI)</strong> - server-seitige Conversion-Spiegelung (in Vorbereitung)</li>
              </ul>

              <h3 className="font-semibold text-[#4A331D] mt-4">4.2 Google Consent Mode v2</h3>
              <p>
                Wir implementieren den <strong>Google Consent Mode v2</strong>. Vor Ihrer Einwilligung werden alle
                Consent-Signale auf <em>denied</em> gesetzt (ad_storage, analytics_storage, ad_user_data, ad_personalization).
                Erst nach Ihrer Zustimmung werden diese auf <em>granted</em> aktualisiert und Tracking-Scripts geladen.
              </p>

              <h3 className="font-semibold text-[#4A331D] mt-4">4.3 UTM-Parameter</h3>
              <p>
                URL-Parameter wie <code>utm_source</code>, <code>utm_medium</code>, <code>utm_campaign</code> werden
                ausschliesslich zur Kampagnen-Zuordnung gespeichert, wenn Sie ein Formular absenden oder den
                Projekt-Assistenten nutzen. Sie enthalten keine personenbezogenen Daten.
              </p>

              <h3 className="font-semibold text-[#4A331D] mt-4">4.4 Conversion-Tracking</h3>
              <p>
                Conversion-Events (z.B. <code>generate_lead</code>) werden ausschliesslich bei einer tatsaechlichen
                Anfrage ausgeloest - nicht bei reinen Seitenaufrufen. Damit wird sichergestellt, dass nur konkrete
                Interaktionen gezaehlt werden.
              </p>

              <p>
                <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. a DSGVO (Einwilligung), § 25 Abs. 1 TDDDG.
                Sie koennen Ihre Einwilligung jederzeit ueber den Link "Cookie-Einstellungen" im Footer widerrufen.
              </p>
            </Section>

            {/* 5 - Speicherdauer */}
            <Section icon={Clock} title="5. Speicherdauer und automatische Loeschung">
              <p>Wir loeschen personenbezogene Daten, sobald sie nicht mehr erforderlich sind:</p>
              <ul className="list-disc pl-5">
                <li><strong>Leads & Chat-Sessions:</strong> automatische Loeschung nach <strong>90 Tagen Inaktivitaet</strong> (taegliche Loeschroutine im Backend).</li>
                <li><strong>Hochgeladene Dateien:</strong> werden gemeinsam mit dem zugehoerigen Lead geloescht bzw. anonymisiert.</li>
                <li><strong>Anonyme Tracking-Daten:</strong> nach den Standardlaufzeiten der Drittanbieter (GA4: 14 Monate, Meta: 24 Monate).</li>
                <li><strong>Cookie-Einwilligungs-Beleg:</strong> 13 Monate.</li>
                <li><strong>Steuerlich relevante Daten:</strong> 6-10 Jahre gem. HGB/AO bei Auftragserteilung.</li>
              </ul>
              <p>
                Die Loeschfristen sind im Adminbereich konfigurierbar und werden taeglich um 03:00 Uhr automatisch
                ausgefuehrt.
              </p>
            </Section>

            {/* 6 - Sicherheit */}
            <Section icon={Lock} title="6. Sicherheit der Verarbeitung">
              <p>Wir setzen technische und organisatorische Massnahmen nach Art. 32 DSGVO um:</p>
              <ul className="list-disc pl-5">
                <li>Verschluesselte Uebertragung (HTTPS/TLS) ueber alle Verbindungen</li>
                <li>Passwoerter mit bcrypt-Hashing gespeichert</li>
                <li>JWT-basierte Authentifizierung mit kurzer Token-Laufzeit</li>
                <li>Rollenbasierte Zugriffsrechte (Admin-Only Endpunkte)</li>
                <li>Rate Limiting auf oeffentlichen API-Endpunkten (Chat, Upload, Lead-Erstellung) gegen Missbrauch</li>
                <li>Server-seitige Validierung aller Datei-Uploads (MIME, Groesse, Dateierweiterung)</li>
                <li>Audit-Logging von Admin-Aktionen (Logins, Statusaenderungen, Loeschungen)</li>
                <li>Datenbank-Zugriff ausschliesslich ueber authentifizierte Backend-Services</li>
              </ul>
            </Section>

            {/* 7 - Upload */}
            <Section icon={Upload} title="7. Upload-Schutz">
              <p>Bei jedem Upload pruefen wir automatisch:</p>
              <ul className="list-disc pl-5">
                <li><strong>Dateityp</strong> per Whitelist (jpg, jpeg, png, webp, heic, heif, mp4, mov, pdf)</li>
                <li><strong>Dateigroesse</strong> max. 15 MB je Datei</li>
                <li><strong>Inhalt</strong> auf bekannte schaedliche Signaturen (z.B. ausfuehrbarer Code)</li>
                <li>Dateien werden mit zufaelliger ID umbenannt und nicht oeffentlich indexiert</li>
              </ul>
            </Section>

            {/* 8 - Empfaenger */}
            <Section icon={Phone} title="8. Empfaenger und Drittanbieter">
              <p>Folgende Auftragsverarbeiter koennen ggf. Ihre Daten verarbeiten:</p>
              <ul className="list-disc pl-5">
                <li><strong>Emergent (Hosting & Object Storage & LLM-Gateway)</strong> - DE/EU</li>
                <li><strong>Google Ireland Ltd.</strong> - Google Ads, GA4, Tag Manager, Gemini-LLM (USA, EU-SCCs)</li>
                <li><strong>OpenAI Ireland Ltd.</strong> - GPT-LLM fuer Lead-Zusammenfassung (USA, EU-SCCs)</li>
                <li><strong>Meta Platforms Ireland Ltd.</strong> - Meta Pixel, WhatsApp, Conversion API (USA, EU-SCCs)</li>
                <li><strong>SMTP-Provider</strong> - Versand interner Lead-Benachrichtigungen</li>
              </ul>
              <p>Mit allen Auftragsverarbeitern bestehen oder werden Vertraege gem. Art. 28 DSGVO geschlossen.</p>
            </Section>

            {/* 9 - Rechte */}
            <Section icon={Shield} title="9. Ihre Rechte">
              <p>Sie haben jederzeit das Recht auf:</p>
              <ul className="list-disc pl-5">
                <li><strong>Auskunft</strong> ueber Ihre gespeicherten Daten (Art. 15 DSGVO)</li>
                <li><strong>Berichtigung</strong> unrichtiger Daten (Art. 16 DSGVO)</li>
                <li><strong>Loeschung</strong> ("Recht auf Vergessenwerden", Art. 17 DSGVO)</li>
                <li><strong>Einschraenkung der Verarbeitung</strong> (Art. 18 DSGVO)</li>
                <li><strong>Datenuebertragbarkeit</strong> (Art. 20 DSGVO)</li>
                <li><strong>Widerspruch</strong> gegen die Verarbeitung (Art. 21 DSGVO)</li>
                <li><strong>Widerruf</strong> erteilter Einwilligungen mit Wirkung fuer die Zukunft (Art. 7 Abs. 3 DSGVO)</li>
                <li><strong>Beschwerde</strong> bei der zustaendigen Aufsichtsbehoerde (in NRW: LDI NRW)</li>
              </ul>
              <p>
                Zur Wahrnehmung Ihrer Rechte genuegt eine formlose E-Mail an{' '}
                <a href="mailto:kontakt@bekajbau.de" className="text-[#4A331D] underline">kontakt@bekajbau.de</a>.
              </p>
            </Section>

            <p className="text-xs text-gray-500 text-center pt-4">
              Diese Datenschutzerklaerung wird laufend angepasst, sobald Funktionen geaendert oder erweitert werden.
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Privacy;
